<script setup>
import {onMounted, ref, watch} from 'vue'
import axios from 'axios'

const props = defineProps({
    initialDiscount: {
        type: String,
        default: '0.00'
    }
})

const emit = defineEmits(['notification', 'discountUpdated', 'updateCart'])

// Data properties
const giftCardCode = ref('')
const ownedCards = ref([])
const loadOwnedCards = async () => {
    try {
        const {data} = await axios.get('/api/gift-card/mine')
        ownedCards.value = data.data.filter(card => card.available > 0)
    } catch (error) {
        ownedCards.value = []
    }
}
const activationRequired = ref(false)
watch(giftCardCode, () => { activationRequired.value = false })
const giftCardLoading = ref(false)
const giftCardMessage = ref('')
const giftCardSuccess = ref(false)
const giftCardApplied = ref(false)
const giftCardDiscount = ref(props.initialDiscount)
const giftCardTransaction = ref(null)

// Format price function
const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(Number(price))
}

const fetchGiftCard = async () => {
    try {
        // Dans une implémentation réelle, vous devriez appeler une API pour obtenir les points disponibles
        // Par exemple:
        const response = await axios.get('/api/checkout/cart/get-gift-card')

        if (response.data.data.code) {
            giftCardCode.value = response.data.data.code
            processGiftCard(response.data.data.discount)
        }


    } catch (error) {
        console.error('Erreur lors de la récupération des points disponibles:', error)
    }
}

// Apply gift card function
const applyGiftCard = async () => {
    if (!giftCardCode.value || giftCardLoading.value) return

    giftCardLoading.value = true
    giftCardMessage.value = ''
    activationRequired.value = false

    try {
        // Appel API pour appliquer la carte cadeau
        const response = await axios.post('/api/checkout/cart/use-gift-card', {
            code: giftCardCode.value
        })

        if (!response.data.data.error) {
            processGiftCard(response.data.data.discount)
            emit('updateCart', response.data.data.cart)
        } else {
            activationRequired.value = response.data.data.activation_required === true
            giftCardSuccess.value = false
            giftCardMessage.value = response.data.data.message || 'Erreur lors de l\'application de la carte cadeau.'
            giftCardApplied.value = false

            // Émettre l'événement pour la notification
            emit('notification', giftCardMessage.value, 'error')
        }
    } catch (error) {
        giftCardSuccess.value = false
        giftCardMessage.value = error.response?.data?.data?.message
            || error.response?.data?.message
            || 'Une erreur est survenue. Veuillez réessayer.'
        console.error('Erreur lors de l\'application de la carte cadeau:', error)

        // Émettre l'événement pour la notification
        emit('notification', 'Erreur lors de l\'application de la carte cadeau.', 'error')
    } finally {
        giftCardLoading.value = false
    }
}

const activateAndApplyGiftCard = async () => {
    if (giftCardLoading.value || !activationRequired.value) return
    giftCardLoading.value = true
    try {
        const response = await axios.post('/api/gift-card/activate', {code: giftCardCode.value.trim()})
        if (response.data.data?.authenticated === false) {
            throw new Error('Connectez-vous pour activer cette carte cadeau.')
        }
        activationRequired.value = false
    } catch (error) {
        giftCardMessage.value = error.response?.data?.message || error.message || 'Activation impossible.'
        giftCardSuccess.value = false
        return
    } finally {
        giftCardLoading.value = false
    }
    await applyGiftCard()
}

const processGiftCard = (discount) => {
    giftCardSuccess.value = true
    giftCardMessage.value = 'Carte cadeau appliquée avec succès!'
    giftCardApplied.value = true

    // Calculer la valeur en euros de la carte cadeau
    giftCardDiscount.value = discount

    // Émettre l'événement pour la notification
    emit('notification', 'Carte cadeau appliquée avec succès!', 'success')

    // Émettre l'événement pour mettre à jour le montant de la réduction
    emit('discountUpdated', giftCardDiscount.value)
}

// Remove gift card function
const removeGiftCard = async () => {
    giftCardLoading.value = true

    try {
        // Cette API utilise le Manager pour annuler la transaction
        const response = await axios.post('/api/checkout/cart/cancel-gift-card')

        giftCardCode.value = ''
        giftCardMessage.value = 'Carte cadeau supprimée.'
        giftCardSuccess.value = true
        giftCardApplied.value = false
        giftCardDiscount.value = '0.00'
        giftCardTransaction.value = null
        await loadOwnedCards()

        // Émettre l'événement pour mettre à jour le montant de la réduction
        emit('discountUpdated', giftCardDiscount.value)
        emit('updateCart', response.data.data.cart)

        // Émettre l'événement pour la notification
        emit('notification', 'Carte cadeau supprimée.', 'success')
    } catch (error) {
        // Gérer l'erreur
        emit('notification', 'Erreur lors de la suppression de la carte cadeau.', 'error')
    } finally {
        giftCardLoading.value = false
    }
}

onMounted(() => {
    fetchGiftCard()
    loadOwnedCards()
})
</script>

<template>
    <div class="gift-card-section">
        <h4 class="section-title">
            <i class="bi bi-gift me-2"></i>Utiliser une carte cadeau
        </h4>
        <p class="text-muted mb-3">Entrez le code de votre carte cadeau pour bénéficier d'une réduction sur votre
            commande.</p>

        <div v-if="ownedCards.length && !giftCardApplied" class="mb-3">
            <label for="owned-gift-card" class="form-label">Mes cartes cadeaux disponibles</label>
            <select id="owned-gift-card" class="form-select" v-model="giftCardCode" :disabled="giftCardLoading">
                <option value="">Choisir une carte ou saisir un autre code ci-dessous</option>
                <option v-for="card in ownedCards" :key="card.id" :value="card.code">{{ card.code }} — {{ formatPrice(card.available) }} € disponibles</option>
            </select>
        </div>
        <div class="gift-card-container">
            <div class="gift-card-form" :class="{'is-loading': giftCardLoading}">
                <div class="input-group">
                    <span class="input-group-text">
                        <i class="bi bi-credit-card"></i>
                    </span>
                    <input
                        type="text"
                        class="form-control"
                        v-model="giftCardCode"
                        placeholder="Code de la carte cadeau"
                        :disabled="giftCardLoading || giftCardApplied"
                    >
                    <button
                        class="btn btn-primary"
                        type="button"
                        @click="applyGiftCard"
                        :disabled="!giftCardCode || giftCardLoading || giftCardApplied"
                    >
                        <span v-if="!giftCardLoading">Appliquer</span>
                        <span v-else class="spinner-border spinner-border-sm" role="status"
                              aria-hidden="true"></span>
                    </button>
                </div>
            </div>

            <div v-if="giftCardMessage" class="gift-card-message mt-2"
                 :class="{'is-success': giftCardSuccess, 'is-error': !giftCardSuccess}">
                <i :class="['bi', giftCardSuccess ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill', 'me-2']"></i>
                {{ giftCardMessage }}
            </div>

            <button v-if="activationRequired" type="button" class="btn btn-outline-primary mt-2"
                    :disabled="giftCardLoading" @click="activateAndApplyGiftCard">
                Activer et utiliser cette carte
            </button>

            <div v-if="giftCardApplied" class="applied-gift-card mt-3">
                <div class="applied-gift-card-details">
                    <div class="gift-card-badge">
                        <i class="bi bi-check-circle-fill me-1"></i>
                        Carte cadeau appliquée
                    </div>
                    <div class="discount-amount">
                        -{{ formatPrice(giftCardDiscount) }}€
                    </div>
                </div>
                <button class="btn btn-sm btn-outline-danger" type="button" @click="removeGiftCard"
                        aria-label="Retirer la carte cadeau">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>
        </div>
    </div>
</template>
